import store from '@/store/index';
import { SlotItem } from './slotItem';
import { Vector2 } from '@babylonjs/core';
import { Legs } from './legs/legs';
import Element from './element';
import { Product, ProductType } from '@/store/modules/product';

class StringUtil {
    public generateUUID(): string {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
            /[xy]/g,
            (c) => {
                // tslint:disable-next-line:no-bitwise
                const r = (Math.random() * 16) | 0;
                // tslint:disable-next-line:no-bitwise
                const v = c === 'x' ? r : (r & 0x3) | 0x8;
                return v.toString(16);
            });
    }

    public isHex(input: string): boolean {
        return (
            (input.length === 7 || input.length === 4) &&
            input[0] === '#'
        );
    }

    public createUrl(relativePath: string): string {
        relativePath = this.correctRelativePath(relativePath);
        return `${store.getters.configuration.baseUrl}${relativePath}`;
    }

    public createDownloadUrl(relativePath: string): string {
        relativePath = this.correctRelativePath(relativePath);
        return `${store.getters.configuration.baseUrl}${relativePath}`;
    }

    public createConfigUrl(id: string): string {
        return `${store.getters.configuration.apiUrlConfig}${id}`;
    }

    public toChar(n: number): string {
        return String.fromCharCode(n);
    }

    public toShadowLight(path: string): string {
        return `${this.removeExtension(path)}_shadow_light.png`;
    }

    public toRafixLight(path: string): string {
        return `${this.removeExtension(path)}_rafix_light.jpg`;
    }

    public toGasketLight(path: string): string {
        return `${this.removeExtension(path)}_uszczelka_light.jpg`;
    }

    public createSupportModel(legs: Legs): string {
        return `n_podp_h${legs.height}_${this.width}x${this.depth}.gltf`;
    }

    public createSupportLight(legs: Legs): string {
        const height = legs.height > 0 ? legs.height : 1;
        return `n_podp_h${height}_${this.width}_light.jpg`;
    }

    public createFrontModel(element: Element, shape: Vector2, slot: SlotItem): string {
        if (element.model === 'sn_s_455_1.gltf') {
            return 'sn_f_1x0,5_AA.gltf';
        }
        if (element.model.includes('b_s_')) {
            return `b_f_${element.shape.x}_A.gltf`;
        }
        return this.toFrontPosition(`f_${shape.x}x0,5_?.gltf`, slot);
    }

    public createFrontLight(element: Element, shape: Vector2, slot: SlotItem): string {
        if (element.model === 'sn_s_455_1.gltf') {
            return '';
        }
        if (element.model.includes('b_s_')) {
            return `b_f_${shape.x}_A_mesh_light.jpg`;
        }
        return this.toFrontPosition(`f_${shape.x}x0,5_?_light.jpg`, slot);
    }

    public createKeaRingModel(): string {
        const width = store.getters.product.width;
        const depth = store.getters.product.depth;
        return `n_pierscien_${width}x${depth}.gltf`;
    }

    public fillLegsPath(path: string): string {
        return this.fillDepth(this.fillWidth(path));
    }

    public toFrontPosition(template: string, slot: SlotItem): string {
        const x = slot.position.x - (0.5 * (slot.shape.x - 1));
        const y = slot.position.y - (0.5 * (slot.shape.y - 1));
        const position = `${stringUtil.toChar(64 + x)}${stringUtil.toChar(64 + y)}`;
        return template.replace('?', position);
    }

    public toShelfPosition(template: string, slot: SlotItem): string {
        const x = slot.position.x - (slot.shape.x - 1) / 2;
        const y = slot.position.y * 2 - 1;
        const position = `${stringUtil.toChar(64 + x)}${stringUtil.toChar(64 + y)}`;
        return template.replace('?', position);
    }

    public fillTemplate(template: string, slot: SlotItem): string {
        if (!template.includes('?')) { return template; }
        return this.toFrontPosition(template, slot);
    }

    public fillDimensions(template: string): string {
        return this.fillWidth(this.fillDepth(this.fillHeight(template)));
    }

    public toMagnificationUrl(url: string): string {
        if (url.includes('_icon')) {
            const previewFilename = url.replace('icon', 'preview');
            const previewWithoutExtension = this.removeExtension(previewFilename);
            const magnificationFilename = `${previewWithoutExtension}.png`;
            return this.createDownloadUrl(magnificationFilename);
        }
        if (url.includes('_CREATIVE_')) {
            const filename = this.removeExtension(url);
            const magnificationFilename = `${filename}_mag.png`;
            return this.createDownloadUrl(magnificationFilename);
        }
        return this.createDownloadUrl(url);
    }

    public getOrigin(url: string): string {
        const u = new URL(url);
        return u.origin;
    }

    public getUploadPresetUrl(): string {
        const baseUrl = store.getters.configuration.baseUrl;
        const origin = this.getOrigin(baseUrl);
        const url = `${origin}/api/product/preset/upload`;
        return url;
    }

    private get width(): string {
        return store.getters.product.width;
    }

    private get depth(): string {
        return store.getters.product.depth;
    }

    private get height(): string {
        return store.getters.product.height;
    }

    private fillHeight(input: string): string {
        return input.replace('{h}', this.height);
    }

    private fillWidth(input: string): string {
        const product: Product = store.getters.product;
        if (product.type === ProductType.BED) {
            return this.fillWidthForBed(input);
        }
        return input.replace('{w}', this.width);
    }
    private fillWidthForBed(input: string): string {
        const code = store.getters.configuration.productPreset.code;
        const width = code.split('_')[1];
        if (width === '160') {
            return input.replace('_{w}', '');
        }
        return input.replace('{w}', width);
    }

    private fillDepth(input: string): string {
        return input.replace('{d}', this.depth);
    }

    private removeExtension(input: string): string {
        return input.split('.').slice(0, -1).join('.');
    }

    private correctRelativePath(relativePath: string): string {
        if (relativePath[0] === '/') { return relativePath; }
        return '/' + relativePath;
    }
}

export const stringUtil = new StringUtil();
